import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { getMexicoDateString, addDaysToDateString } from '../lib/mexicoDate';

interface Machine {
  id: number;
  code: string;
  name: string;
  description?: string;
  location?: string;
  disponibilidadColores?: number;
  anchoImpresion?: number;
  orders: GanttOrder[];
}

interface GanttOrder {
  id: string;
  orderNumber: string;
  customerName?: string;
  productName: string;
  quantityPlanned: string;
  unit: string;
  priority: string;
  dueDate?: string;
  plannedStartDate?: string;
  plannedEndDate?: string;
  plannedStartAt?: string | null;
  plannedEndAt?: string | null;
  estimatedDuration?: number;
  status: string;
  sequence?: number;
  machineName?: string;
  // ROT-210: present only when 2+ manufacturing-route steps are in_progress
  // on distinct machines at once — see the same field on the Kanban
  // endpoint response (backend/src/routes/production-orders.ts GET /).
  activeMachines?: { process: string; machineName: string }[];
  // ROT-218: every step whose machine differs from the order's own,
  // regardless of status — see backend/src/services/processMachineAssignments.ts.
  processMachineAssignments?: { process: string; machineName: string }[];
}

interface GanttData {
  machines: Machine[];
  unassignedOrders: GanttOrder[];
  totalOrders: number;
  totalMachines: number;
}

interface GanttViewProps {
  onDataLoaded?: (data: { backlogCount: number; weekCount: number; todayCount: number }) => void;
  filterMachines?: string[];
  onOptimizationApplied?: () => void;
}

const GanttView: React.FC<GanttViewProps> = ({ onDataLoaded, filterMachines }) => {
  const queryClient = useQueryClient();
  const [ganttData, setGanttData] = useState<GanttData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draggedOrder, setDraggedOrder] = useState<GanttOrder | null>(null);
  const [viewDays, setViewDays] = useState(7);
  const [machineSortDir, setMachineSortDir] = useState<'asc' | 'desc'>('asc');
  const ganttRef = useRef<HTMLDivElement>(null);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  };

  const fetchGanttData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/gantt/data', {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Error al cargar datos del Gantt');
      }

      const data = await response.json();
      setGanttData(data);
      setError(null);

      // Calculate counters from Gantt data
      if (onDataLoaded && data) {
        const todayStr = getMexicoDateString();
        const weekFromNowStr = addDaysToDateString(todayStr, 7);

        // Get all orders from machines + unassigned
        const machineOrders: GanttOrder[] = data.machines?.flatMap((m: Machine) => m.orders) || [];
        const unassignedOrders: GanttOrder[] = data.unassignedOrders || [];
        const allOrders: GanttOrder[] = [...machineOrders, ...unassignedOrders];

        // Backlog = unassigned orders (no machine assignment)
        const backlogCount = unassignedOrders.length;
        
        // Today = orders with plannedStartDate = today (from all orders)
        const todayCount = allOrders.filter(o => {
          if (!o.plannedStartDate) return false;
          return o.plannedStartDate.split('T')[0] === todayStr;
        }).length;
        
        // Week = orders with plannedStartDate within next 7 days
        const weekCount = allOrders.filter(o => {
          if (!o.plannedStartDate) return false;
          const startDate = o.plannedStartDate.split('T')[0];
          return startDate >= todayStr && startDate <= weekFromNowStr;
        }).length;

        console.log('Gantt counters:', { backlogCount, todayCount, weekCount, totalOrders: allOrders.length });
        onDataLoaded({ backlogCount, weekCount, todayCount });
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [onDataLoaded]);

  useEffect(() => {
    fetchGanttData();
  }, [fetchGanttData]);

  const handleDragStart = (order: GanttOrder) => {
    setDraggedOrder(order);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (machine: Machine, dropDate?: Date) => {
    if (!draggedOrder) return;

    try {
      const body: any = {
        orderId: draggedOrder.id,
        machineId: machine.id,
        machineName: machine.name,
        machineCode: machine.code,
      };

      // If a date was specified, update the planned dates
      if (dropDate) {
        // Create a new date object to avoid timezone issues
        const year = dropDate.getFullYear();
        const month = String(dropDate.getMonth() + 1).padStart(2, '0');
        const day = String(dropDate.getDate()).padStart(2, '0');
        const startDate = `${year}-${month}-${day}`;
        
        // Calculate end date based on estimated duration or default to same day
        const durationHours = draggedOrder.estimatedDuration ? draggedOrder.estimatedDuration / 60 : 8;
        const endDateObj = new Date(dropDate);
        endDateObj.setHours(endDateObj.getHours() + durationHours);
        const endYear = endDateObj.getFullYear();
        const endMonth = String(endDateObj.getMonth() + 1).padStart(2, '0');
        const endDay = String(endDateObj.getDate()).padStart(2, '0');
        
        body.plannedStartDate = startDate;
        body.plannedEndDate = `${endYear}-${endMonth}-${endDay}`;
        
        console.log('Updating order with date:', { orderId: draggedOrder.id, startDate, endDate: body.plannedEndDate, dropDate: dropDate.toISOString() });
      } else {
        console.log('No dropDate provided for order:', draggedOrder.id);
      }

      const response = await fetch('/api/gantt/assignment', {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody?.error || 'Error al actualizar asignación');
      }

      await fetchGanttData();
      // Invalidate cache so counters update
      queryClient.invalidateQueries({ queryKey: ['plannableOrders'] });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDraggedOrder(null);
    }
  };

  const getOrderBarColor = (order: GanttOrder) => {
    if (order.status === 'COMPLETED') return 'bg-gray-400';
    switch (order.priority) {
      case '1': return 'bg-red-500 hover:bg-red-600';
      case '2': return 'bg-amber-500 hover:bg-amber-600';
      case '3': return 'bg-green-500 hover:bg-green-600';
      default: return 'bg-blue-500 hover:bg-blue-600';
    }
  };

  // ROT-210: appended to a bar's tooltip when the order has 2+ manufacturing-
  // route steps in_progress on distinct machines right now.
  const simultaneousTitleSuffix = (order: GanttOrder) => {
    if (!order.activeMachines || order.activeMachines.length < 2) return '';
    const lines = order.activeMachines.map(m => `${m.process} → ${m.machineName}`);
    return `\n⚡ Simultánea:\n${lines.join('\n')}`;
  };

  // ROT-218: appended to a bar's tooltip whenever any step's machine
  // differs from the order's own — unlike simultaneousTitleSuffix above,
  // this needs no in_progress status and no 2+ threshold.
  const processAssignmentsTitleSuffix = (order: GanttOrder) => {
    if (!order.processMachineAssignments || order.processMachineAssignments.length === 0) return '';
    const lines = order.processMachineAssignments.map(m => `${m.process} → ${m.machineName}`);
    return `\nMáquina por proceso:\n${lines.join('\n')}`;
  };

  const generateDates = () => {
    const dates: Date[] = [];
    for (let i = 0; i < viewDays; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const dates = generateDates();

  const formatDateHeader = (date: Date) => {
    const options: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' };
    return date.toLocaleDateString('es-MX', options);
  };

  const isToday = (date: Date) => {
    return date.toDateString() === today.toDateString();
  };

  const getTimedSlot = (order: GanttOrder): { start: Date; end: Date } | null => {
    if (!order.plannedStartAt || !order.plannedEndAt || !order.plannedStartDate) return null;
    const start = new Date(order.plannedStartAt);
    const end = new Date(order.plannedEndAt);
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || end <= start) return null;
    const pad = (n: number) => String(n).padStart(2, '0');
    const startDay = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`;
    return startDay === order.plannedStartDate.split('T')[0] ? { start, end } : null;
  };

  const fmtSlotTime = (d: Date) => d.toLocaleString('es-MX', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  const slotTitleSuffix = (order: GanttOrder) => {
    const slot = getTimedSlot(order);
    return slot ? `\nProgramada: ${fmtSlotTime(slot.start)} → ${fmtSlotTime(slot.end)}` : '';
  };

  const getOrderPosition = (order: GanttOrder) => {
    if (!order.plannedStartDate) return null;

    // Parse the date string as local date to avoid timezone issues
    const dateParts = order.plannedStartDate.split('T')[0].split('-');
    const startDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
    startDate.setHours(0, 0, 0, 0);
    
    // Get today without time for accurate comparison
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    
    const daysDiff = Math.floor((startDate.getTime() - todayStart.getTime()) / (1000 * 60 * 60 * 24));
    
    // If date is in the past or beyond view range, return null
    if (daysDiff < 0 || daysDiff >= viewDays) return null;

    // Exact slot (date + time) written when a simulation is applied: position and length follow the real
    // hours. Ignored if it no longer matches plannedStartDate (the day was changed by hand afterwards).
    const slot = getTimedSlot(order);
    if (slot) {
      const dayMs = 1000 * 60 * 60 * 24;
      const startFrac = (slot.start.getTime() - todayStart.getTime()) / dayMs;
      const endFrac = Math.min(viewDays, (slot.end.getTime() - todayStart.getTime()) / dayMs);
      return {
        left: `${(startFrac / viewDays) * 100}%`,
        width: `${(Math.max(endFrac - startFrac, 0) / viewDays) * 100}%`,
        daysDiff,
      };
    }

    const duration = order.estimatedDuration ? Math.ceil(order.estimatedDuration / 480) : 1;
    const widthDays = Math.min(duration, viewDays - daysDiff);

    return {
      left: `${(daysDiff / viewDays) * 100}%`,
      width: `${(widthDays / viewDays) * 100}%`,
      daysDiff,
    };
  };

  // Calculate the order index within the same day for stacking
  const getOrderStackIndex = (orders: GanttOrder[], order: GanttOrder) => {
    if (!order.plannedStartDate) return 0;
    const orderDate = order.plannedStartDate.split('T')[0];
    
    // Get all orders for the same day, sorted by sequence or creation
    const sameDayOrders = orders
      .filter(o => o.plannedStartDate && o.plannedStartDate.split('T')[0] === orderDate)
      .sort((a, b) => {
        // Sort by sequence if available, otherwise by order number
        if (a.sequence !== undefined && b.sequence !== undefined) {
          return a.sequence - b.sequence;
        }
        return (a.orderNumber || '').localeCompare(b.orderNumber || '');
      });
    
    return sameDayOrders.findIndex(o => o.id === order.id);
  };

  // Calculate max orders per day for a machine to determine row height
  const getMaxOrdersPerDay = (orders: GanttOrder[]) => {
    const ordersPerDay: Record<string, number> = {};
    orders.forEach(order => {
      if (order.plannedStartDate) {
        const dateKey = order.plannedStartDate.split('T')[0];
        ordersPerDay[dateKey] = (ordersPerDay[dateKey] || 0) + 1;
      }
    });
    return Math.max(1, ...Object.values(ordersPerDay));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-600">Cargando vista de Gantt...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-4">
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchGanttData}
          className="mt-2 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      <div className="p-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Vista de Gantt
            </h2>
            <p className="text-blue-100 text-sm mt-1">
              {ganttData?.totalMachines || 0} máquinas · {ganttData?.totalOrders || 0} órdenes
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <select
              value={viewDays}
              onChange={(e) => setViewDays(Number(e.target.value))}
              className="px-3 py-2 rounded-lg border border-blue-400 bg-blue-500 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white"
            >
              <option value={7}>7 días</option>
              <option value={14}>14 días</option>
              <option value={30}>30 días</option>
            </select>
          </div>
        </div>
      </div>

      <div className="overflow-auto max-h-[calc(100vh-280px)]" ref={ganttRef}>
        <div className="min-w-[1000px]">
          <div className="flex border-b border-gray-200 bg-gray-50 sticky top-0 z-10 shadow-sm">
            <div
              className="w-48 flex-shrink-0 p-3 font-semibold text-gray-700 border-r border-gray-200 sticky left-0 bg-gray-50 z-20 cursor-pointer select-none hover:bg-gray-100 transition-colors"
              onClick={() => setMachineSortDir(d => d === 'asc' ? 'desc' : 'asc')}
              title="Ordenar por clave de máquina"
            >
              <div className="flex items-center gap-1">
                Máquina / Recurso
                {machineSortDir === 'asc'
                  ? <ChevronUp className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                  : <ChevronDown className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />}
              </div>
            </div>
            <div className="flex-1 flex">
              {dates.map((date, index) => (
                <div
                  key={index}
                  className={`flex-1 text-center p-2 text-sm font-medium border-r border-gray-200 last:border-r-0 ${
                    isToday(date) ? 'bg-blue-100 text-blue-800' : 'text-gray-600'
                  }`}
                >
                  {formatDateHeader(date)}
                </div>
              ))}
            </div>
          </div>

          {[...(ganttData?.machines || [])].sort((a, b) => {
            const cmp = a.code.localeCompare(b.code, undefined, { numeric: true, sensitivity: 'base' });
            return machineSortDir === 'asc' ? cmp : -cmp;
          }).filter(machine => {
            if (!filterMachines || filterMachines.length === 0) return true;
            return filterMachines.includes(machine.name) || filterMachines.includes(machine.code);
          }).map((machine) => {
            const maxOrdersPerDay = getMaxOrdersPerDay(machine.orders);
            const orderHeight = 36; // Height per order in pixels
            const rowHeight = Math.max(60, maxOrdersPerDay * orderHeight + 12);
            
            return (
              <div
                key={machine.id}
                className="flex border-b border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <div className="w-48 flex-shrink-0 p-3 border-r border-gray-200 bg-gray-50 sticky left-0 z-10">
                  <div className="font-medium text-gray-800">{machine.name}</div>
                  <div className="text-xs text-gray-500">{machine.code}</div>
                  {machine.disponibilidadColores && (
                    <div className="text-xs text-gray-400 mt-1">
                      {machine.disponibilidadColores} colores
                    </div>
                  )}
                  <div className="text-xs text-blue-500 mt-1">
                    {machine.orders.length} órdenes
                  </div>
                </div>
                <div className="flex-1 relative" style={{ minHeight: `${rowHeight}px` }}>
                  <div className="absolute inset-0 flex">
                    {dates.map((date, index) => (
                      <div
                        key={index}
                        className={`flex-1 border-r border-gray-100 last:border-r-0 ${
                          isToday(date) ? 'bg-blue-50' : ''
                        } ${draggedOrder ? 'hover:bg-green-100 cursor-pointer' : ''}`}
                        onDragOver={handleDragOver}
                        onDrop={() => handleDrop(machine, date)}
                      />
                    ))}
                  </div>

                  {machine.orders
                    .filter(order => getOrderPosition(order) !== null)
                    .map((order) => {
                    const position = getOrderPosition(order);
                    // Calculate stack index only among orders with valid positions for this machine
                    const ordersWithValidPosition = machine.orders.filter(o => getOrderPosition(o) !== null);
                    const stackIndex = getOrderStackIndex(ordersWithValidPosition, order);
                    const topPosition = 4 + (stackIndex * orderHeight);

                    return (
                      <div
                        key={order.id}
                        draggable
                        onDragStart={() => handleDragStart(order)}
                        className={`absolute rounded-lg px-2 py-1 text-xs text-white shadow-md cursor-move transition-all hover:scale-105 hover:z-10 ${getOrderBarColor(order)}`}
                        style={{
                          left: position!.left,
                          width: position!.width,
                          top: `${topPosition}px`,
                          minWidth: '80px',
                          zIndex: stackIndex + 1,
                        }}
                        title={`#${stackIndex + 1} - ${order.orderNumber}\n${order.productName}\nCliente: ${order.customerName || 'N/A'}\nCantidad: ${order.quantityPlanned} ${order.unit}${slotTitleSuffix(order)}${simultaneousTitleSuffix(order)}${processAssignmentsTitleSuffix(order)}`}
                      >
                        <div className="font-semibold truncate flex items-center gap-1">
                          <span className="bg-white/30 rounded px-1">#{stackIndex + 1}</span>
                          {order.orderNumber}
                          {/* ROT-210: order running on 2+ machines at once (e.g. printing
                              and laminating simultaneously) — see activeMachines on the API. */}
                          {order.activeMachines && order.activeMachines.length >= 2 && (
                            <span title="Ejecutándose simultáneamente en varias máquinas">⚡</span>
                          )}
                        </div>
                        <div className="truncate opacity-80">{order.productName}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Orders with past dates (outside visible range) */}
          {(() => {
            const pastOrders = ganttData?.machines.flatMap(m => 
              m.orders.filter(order => {
                if (!order.plannedStartDate) return false;
                const dateParts = order.plannedStartDate.split('T')[0].split('-');
                const orderDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
                const todayStart = new Date();
                todayStart.setHours(0, 0, 0, 0);
                return orderDate < todayStart;
              }).map(o => ({ ...o, machineName: m.name }))
            ) || [];
            
            if (pastOrders.length === 0) return null;
            
            return (
              <div className="flex border-b border-gray-200 bg-red-50">
                <div className="w-48 flex-shrink-0 p-3 border-r border-gray-200">
                  <div className="font-medium text-red-700">Fechas vencidas</div>
                  <div className="text-xs text-red-500">
                    {pastOrders.length} órdenes
                  </div>
                </div>
                <div className="flex-1 p-2 flex flex-wrap gap-2">
                  {pastOrders.map((order) => (
                    <div
                      key={order.id}
                      draggable
                      onDragStart={() => handleDragStart(order)}
                      className={`px-3 py-2 rounded-lg text-xs text-white shadow cursor-move hover:shadow-lg transition-all ${getOrderBarColor(order)}`}
                      title={`${order.orderNumber} - ${order.productName}\nFecha: ${order.plannedStartDate?.split('T')[0]}\nMáquina: ${order.machineName}${simultaneousTitleSuffix(order)}${processAssignmentsTitleSuffix(order)}`}
                    >
                      <div className="font-semibold flex items-center gap-1">
                        {order.orderNumber}
                        {order.activeMachines && order.activeMachines.length >= 2 && <span>⚡</span>}
                      </div>
                      <div className="opacity-80">{order.plannedStartDate?.split('T')[0]}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {ganttData?.unassignedOrders && ganttData.unassignedOrders.length > 0 && (
            <div className="flex border-b border-gray-200 bg-orange-50">
              <div className="w-48 flex-shrink-0 p-3 border-r border-gray-200">
                <div className="font-medium text-orange-700">Sin asignar</div>
                <div className="text-xs text-orange-500">
                  {ganttData.unassignedOrders.length} órdenes
                </div>
              </div>
              <div className="flex-1 p-2 flex flex-wrap gap-2">
                {ganttData.unassignedOrders.map((order) => (
                  <div
                    key={order.id}
                    draggable
                    onDragStart={() => handleDragStart(order)}
                    className={`px-3 py-2 rounded-lg text-xs text-white shadow cursor-move hover:shadow-lg transition-all ${getOrderBarColor(order)}`}
                    title={`${order.orderNumber} - ${order.productName}${simultaneousTitleSuffix(order)}${processAssignmentsTitleSuffix(order)}`}
                  >
                    <div className="font-semibold flex items-center gap-1">
                      {order.orderNumber}
                      {order.activeMachines && order.activeMachines.length >= 2 && <span>⚡</span>}
                    </div>
                    <div className="opacity-80">{order.productName}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="p-4 bg-gray-50 border-t border-gray-200">
        <div className="flex items-center gap-6 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-red-500"></div>
            <span>Prioridad Alta</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-amber-500"></div>
            <span>Prioridad Media</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-500"></div>
            <span>Prioridad Baja</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-gray-400"></div>
            <span>Completada</span>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Arrastra las órdenes para reasignarlas a diferentes máquinas
        </p>
      </div>

    </div>
  );
};

export default GanttView;
